import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';

const apiUrl = process.env.REACT_APP_API_URL || 'https://api.sushismile154.ru';

// Хелпер для конвертации UTC ISO строки в строку datetime-local в Новосибирском времени (UTC+7)
const utcToNskLocal = (isoString: string | null): string => {
  if (!isoString) return '';
  const utcDate = new Date(isoString);
  // Добавляем 7 часов для Новосибирска
  const nskDate = new Date(utcDate.getTime() + 7 * 60 * 60 * 1000);
  // Формируем yyyy-MM-ddTHH:mm
  const y = nskDate.getUTCFullYear();
  const m = String(nskDate.getUTCMonth() + 1).padStart(2, '0');
  const d = String(nskDate.getUTCDate()).padStart(2, '0');
  const h = String(nskDate.getUTCHours()).padStart(2, '0');
  const min = String(nskDate.getUTCMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

// Хелпер для конвертации datetime-local (Нск) в UTC ISO строку
const nskLocalToUtc = (localString: string): string | null => {
  if (!localString) return null;
  // localString формат: "2025-01-15T10:00"
  const [datePart, timePart] = localString.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = timePart.split(':').map(Number);
  // Создаём дату как Нск и вычитаем 7 часов для получения UTC
  const nskDate = new Date(Date.UTC(y, m - 1, d, h, min, 0, 0));
  const utcDate = new Date(nskDate.getTime() - 7 * 60 * 60 * 1000);
  return utcDate.toISOString();
};

export const SettingsPage = () => {
  const [cookingTime, setCookingTime] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCount, setAvailableCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [toggling, setToggling] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Новые поля расписания
  const [reopenAt, setReopenAt] = useState<string>(''); // datetime-local формат (Нск)
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleOpenTime, setScheduleOpenTime] = useState<string>('');
  const [scheduleCloseTime, setScheduleCloseTime] = useState<string>('');

  const isOpen = availableCount > 0;

  useEffect(() => {
    fetchSettings();
    fetchAvailabilityStatus();
  }, []);

  const fetchAvailabilityStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/products/admin/availability-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableCount(data.availableCount);
        setTotalCount(data.totalCount);
      }
    } catch (err) {
      console.error('Failed to fetch availability status', err);
    }
  };

  const handleToggleAvailability = async (makeAvailable: boolean) => {
    setToggling(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/products/admin/toggle-all-availability`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isAvailable: makeAvailable }),
      });
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(
          makeAvailable
            ? `✅ Приём заказов открыт! (${data.updated} позиций доступно)`
            : `🔒 Приём заказов закрыт! (${data.updated} позиций отключено)`
        );
        setSuccess(true);
        await fetchAvailabilityStatus();
      } else {
        throw new Error('Ошибка обновления');
      }
    } catch (err) {
      setError('Ошибка при изменении доступности товаров');
    } finally {
      setToggling(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCookingTime(data.cookingTime !== null ? String(data.cookingTime) : '');
        setReopenAt(utcToNskLocal(data.reopenAt));
        setScheduleEnabled(data.scheduleEnabled || false);
        setScheduleOpenTime(data.scheduleOpenTime || '');
        setScheduleCloseTime(data.scheduleCloseTime || '');
      }
    } catch (err) {
      setError('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const cookingTimeValue = cookingTime.trim() === '' ? null : parseInt(cookingTime, 10);
      const reopenAtUtc = reopenAt ? nskLocalToUtc(reopenAt) : null;
      
      const response = await fetch(`${apiUrl}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cookingTime: cookingTimeValue,
          reopenAt: reopenAtUtc,
          scheduleEnabled,
          scheduleOpenTime: scheduleOpenTime || null,
          scheduleCloseTime: scheduleCloseTime || null,
        }),
      });

      if (response.ok) {
        setSuccessMessage('Настройки сохранены');
        setSuccess(true);
      } else {
        throw new Error('Ошибка сохранения');
      }
    } catch (err) {
      setError('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <SettingsIcon color="primary" />
            <Typography variant="h5" component="h1">
              Настройки
            </Typography>
          </Box>

          <Box mb={3}>
            <TextField
              label="Время приготовления (минут)"
              type="number"
              value={cookingTime}
              onChange={(e) => setCookingTime(e.target.value)}
              helperText="Укажите время приготовления заказа в минутах. Это время будет показано клиентам в корзине. Оставьте пустым, чтобы не показывать."
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              Расписание работы (Новосибирское время)
            </Typography>
          </Box>

          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Автоматическое расписание"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              Сайт будет автоматически открываться и закрываться по расписанию (по Новосибирскому времени)
            </Typography>
          </Box>

          {scheduleEnabled && (
            <Box display="flex" gap={2} mb={3}>
              <TextField
                label="Время открытия"
                type="time"
                value={scheduleOpenTime}
                onChange={(e) => setScheduleOpenTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                helperText="Когда открывается приём заказов"
                sx={{ width: 200 }}
              />
              <TextField
                label="Время закрытия"
                type="time"
                value={scheduleCloseTime}
                onChange={(e) => setScheduleCloseTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                helperText="Когда закрывается приём заказов"
                sx={{ width: 200 }}
              />
            </Box>
          )}

          <Box mb={3}>
            <TextField
              label="Открытие приёма заказов (вручную)"
              type="datetime-local"
              value={reopenAt}
              onChange={(e) => setReopenAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Дата и время (Новосибирское) когда приём заказов откроется. До этого момента клиенты увидят страницу «Закрыто». Оставьте пустым, если не нужно."
              fullWidth
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <StorefrontIcon color={isOpen ? 'success' : 'error'} />
            <Typography variant="h5" component="h2">
              Приём заказов
            </Typography>
          </Box>

          <Alert severity={isOpen ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {isOpen
              ? `Приём заказов открыт. Доступно позиций: ${availableCount} из ${totalCount}`
              : `Приём заказов закрыт. Все ${totalCount} позиций отключены`}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Нажмите кнопку, чтобы {isOpen ? 'закрыть' : 'открыть'} приём заказов.
            {isOpen
              ? ' Все позиции станут недоступными для клиентов.'
              : ' Все позиции снова станут доступны для заказа.'}
          </Typography>

          <Box display="flex" gap={2}>
            {isOpen ? (
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={toggling ? <CircularProgress size={20} color="inherit" /> : <BlockIcon />}
                onClick={() => handleToggleAvailability(false)}
                disabled={toggling}
                sx={{ py: 1.5, px: 4 }}
              >
                {toggling ? 'Закрытие...' : 'Закрыть приём заказов'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={toggling ? <CircularProgress size={20} color="inherit" /> : <StorefrontIcon />}
                onClick={() => handleToggleAvailability(true)}
                disabled={toggling}
                sx={{ py: 1.5, px: 4 }}
              >
                {toggling ? 'Открытие...' : 'Открыть приём заказов'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success">
          {successMessage || 'Настройки сохранены'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
